import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import {
  syncedDatesState,
  useAuthContext,
  useSyncAppleCalendarsList,
  useSyncGCalendarsList,
  useSyncOutlookCalendarsList,
} from '../data-access';
import { CalendarType } from '@demind-inc/core';
import {
  fetchAppleCalendarsFromDesktop,
  getAppleCalendarSyncWindow,
  isAppleCalendarDesktopSupported,
} from '../helpers';

interface SyncCalendarsListParams {
  calendarType: CalendarType;
  rootEmail: string;
}

export const useSyncCalendarLists = () => {
  const { syncGCalendarsList: syncGCalendarsListMutation, isPending: isSyncingGCal } =
    useSyncGCalendarsList();
  const {
    syncOutlookCalendarsList: syncOutlookCalendarsListMutation,
    isPending: isSyncingOutlook,
  } = useSyncOutlookCalendarsList();
  const {
    syncAppleCalendarsList: syncAppleCalendarsListMutation,
    isPending: isSyncingApple,
  } = useSyncAppleCalendarsList();
  const { user } = useAuthContext();
  const syncedDates = useRecoilValue(syncedDatesState);

  const isSyncing = useMemo(
    () => isSyncingGCal || isSyncingOutlook || isSyncingApple,
    [isSyncingGCal, isSyncingOutlook, isSyncingApple]
  );

  const syncCalendarsList = async ({ calendarType, rootEmail }: SyncCalendarsListParams) => {
    if (calendarType === 'google') {
      await syncGCalendarsListMutation({
        userId: user.userId,
        rootEmail,
      });
    } else if (calendarType === 'outlook') {
      await syncOutlookCalendarsListMutation({ userId: user.userId, rootEmail });
    } else if (calendarType === 'apple') {
      if (!isAppleCalendarDesktopSupported()) {
        throw new Error('Apple Calendar native sync is only available in the macOS desktop app.');
      }

      const { startTime, endTime } = getAppleCalendarSyncWindow(syncedDates);
      const appleCalendars = await fetchAppleCalendarsFromDesktop({ startTime, endTime });

      await syncAppleCalendarsListMutation({
        userId: user.userId,
        email: user.email,
        metricId: user.metricId,
        userPersona: user.persona?.job,
        startTime,
        endTime,
        shouldSyncDeletedList: true,
        appleCalendars,
      });
    }
  };

  return {
    syncCalendarsList,
    isSyncing,
  };
};
